import OfferDetailPopup from "@/components/shop/OfferDetailPopup.vue";
import WelcomeBackBasePopup from "@/components/WelcomeBack/WelcomeBackBasePopup.vue";
import { IS_DEV } from "@/config/constants";
import { useEventStore } from "@/event";
import GameError, { GameErrorType } from "@/libs/GameError";
import router from "@/router";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useShopStore from "@/store/useShopStore";
import useUserStore from "@/store/useUserStore";
import type { ShopItem } from "@/types/shop";
import { RouteNames, SkinTypes } from "@/types/ui";
import { storeToRefs } from "pinia";
import { markRaw } from "vue";
export type { DeepLinkData } from "@/store/useRuntimeStore";

export const WELCOME_BACK_OFFER_EXPIRY_DURATION = IS_DEV
	? 2 * 60 * 1000 // 2 minutes for dev mode
	: 24 * 60 * 60 * 1000; // 24 hours for production

export enum DeepLinkType {
	// "Welcome back" popup shown BEFORE offline income
	WELCOME_BACK = "welcome-back",
	//  "Welcome back reward" popup shown BEFORE offline income
	WELCOME_BACK_REWARD = "welcome-back-reward",
	// "Welcome back offer" shown AFTER offline income for non-paying users
	WELCOME_BACK_OFFER = "welcome-back-offer",
	// Opens the currently active event popup
	EVENT = "event",
	// Opens the settings view, specifically the referral program tab
	REFERRAL = "referral",
	// Triggers processing of referral data that was previously
	REFERRAL_DATA = "referral-data",
	//  Opens the stats section (Info view, stats tab)
	STATS = "stats",
	// Opens the upgrades view
	UPGRADES = "upgrades",
	//  Opens the shop view
	MARKET = "market",
	// Opens the settings view
	SETTINGS = "settings",
}

// ---------------------------------------------------------------------------
// Internal module state for post-offline callback coordination
// ---------------------------------------------------------------------------

let pendingPostOfflineCallback: (() => void) | undefined;

function setPendingPostOfflineCallback(
	callback: (() => void) | undefined,
): void {
	pendingPostOfflineCallback = callback;
}

export function getPendingPostOfflineCallback(): (() => void) | undefined {
	return pendingPostOfflineCallback;
}

export function consumePostOfflineCallback(): (() => void) | undefined {
	const callback = pendingPostOfflineCallback;
	pendingPostOfflineCallback = undefined;
	return callback;
}

// ---------------------------------------------------------------------------
// Pre-offline-income handlers
// ---------------------------------------------------------------------------

export const handleWelcomeBack = (): void => {
	const popupStore = usePopupStore();

	popupStore.showPopup({
		id: PopupDictionary.WELCOME_BACK,
		type: PopupType.INFO,
		width: 24.5,
		height: "auto",
		isPaddingless: true,
		skin: SkinTypes.DARK,
		isPriority: true,
		hasOverlap: true,
		hasOuterBar: true,
		component: markRaw(WelcomeBackBasePopup),
		componentProps: {
			deepLinkType: DeepLinkType.WELCOME_BACK,
			showClaimButton: false,
			isOffer: false,
		},
		closeCallback: undefined,
	});
};

export const handleWelcomeBackReward = (): void => {
	const popupStore = usePopupStore();
	const userStore = useUserStore();
	const shopStore = useShopStore();
	const welcomeBackReward = shopStore.welcomeBackReward;

	// If the user already claimed a welcome back reward, treat as standard welcome-back
	if (userStore.isWelcomeBackRewardClaimed) {
		handleWelcomeBack();
		return;
	}

	const WELCOME_BACK_REWARD_STORE_ID = welcomeBackReward?.store_id;

	popupStore.showPopup({
		id: PopupDictionary.WELCOME_BACK_REWARD,
		type: PopupType.INFO,
		width: 24.5,
		height: "auto",
		isPaddingless: true,
		skin: SkinTypes.DARK,
		isPriority: true,
		hasOverlap: true,
		hasOuterBar: true,
		component: markRaw(WelcomeBackBasePopup),
		componentProps: {
			deepLinkType: DeepLinkType.WELCOME_BACK_REWARD,
			showClaimButton: !!WELCOME_BACK_REWARD_STORE_ID,
			...(WELCOME_BACK_REWARD_STORE_ID
				? { storeId: WELCOME_BACK_REWARD_STORE_ID }
				: {}),
			isOffer: false,
		},
		closeCallback: undefined,
	});
};

const openWelcomeBackOffer = (offer: ShopItem): void => {
	const popupStore = usePopupStore();

	popupStore.showPopup({
		id: PopupDictionary.WELCOME_BACK_OFFER,
		type: PopupType.INFO,
		width: 24.5,
		height: "auto",
		isPaddingless: true,
		skin: SkinTypes.DARK,
		isPriority: false, // IMPORTANT
		hasOverlap: true,
		hasOuterBar: true,
		component: markRaw(OfferDetailPopup),
		componentProps: { contentData: offer },
	});
};

export const handleWelcomeBackOffer = (): void => {
	const userStore = useUserStore();
	// Paying customers get the standard welcome-back treatment (no offer)

	if (userStore.isPayingCustomer) {
		handleWelcomeBack();
		return;
	}

	const shopStore = useShopStore();
	const { welcomeBackOffer: offer } = storeToRefs(shopStore);
	// Check if the offer is still within its 24-hour window
	const savedExpiration = userStore.deeplinkWelcomeBackOfferExpires;
	const now = Date.now();

	if (offer.value && savedExpiration && now < +savedExpiration) {
		// Offer is still valid — show it with countdown timer
		openWelcomeBackOffer(offer.value);
	} else {
		userStore.setDeeplinkWelcomeBackOfferExpires(
			new Date(now + WELCOME_BACK_OFFER_EXPIRY_DURATION),
		);

		if (!offer.value) {
			handleWelcomeBack();
			return;
		}

		openWelcomeBackOffer(offer.value);
	}
};

export const handleEvent = (): void => {
	const eventStore = useEventStore();

	if (eventStore.isActive) {
		eventStore.setEventPopupActive(true);
	}
};

export const handleReferral = (): void => {
	const runtimeStore = useRuntimeStore();
	const referralData = runtimeStore.referralData;
	if (referralData) {
		runtimeStore.setReferralData(null);
		handleReferralData();
	}

	router.push({ name: RouteNames.SETTINGS });

	// TODO: After referral program is implemented, emit an event that
	// SettingsView can listen for to auto-scroll to the referral tab:
	// useEmitter().emit(EmitterEvents.OpenReferralTab);
};

export const handleReferralData = (): void => {
	const runtimeStore = useRuntimeStore();
	runtimeStore.setReferralData(null);

	// TODO: Implement referral claiming logic here.
	// The `data` object contains referral info from Flutter:
	// - referral_code: string - the unique referral code
	// - source: string - where the referral came from (e.g. "invite", "share")
	// - reward: object - the reward details
	//
	// Example:
	// if (data.referral_code) {
	//   socketService.emitReferralClaim({ code: data.referral_code as string });
	// }
};

export const handleStats = (): void => {
	// Tab index 3 corresponds to the stats section in the Info view
	router.push({ name: RouteNames.INFO, params: { tab: 3 } });
};

export const handleUpgrades = (): void => {
	router.push({ name: RouteNames.UPGRADES });
};

export const handleMarket = (): void => {
	if (useSettlementsStore().mainLevel < 3) {
		return;
	}

	router.push({ name: RouteNames.SHOP });
};

export const handleSettings = (): void => {
	router.push({ name: RouteNames.SETTINGS });
};

// ---------------------------------------------------------------------------
// Router / dispatcher
// ---------------------------------------------------------------------------

const VALID_DEEP_LINK_VALUES = new Set(Object.values(DeepLinkType));

const PRE_OFFLINE_HANDLERS: Record<string, () => void | Promise<void>> = {
	[DeepLinkType.WELCOME_BACK]: handleWelcomeBack,
	[DeepLinkType.WELCOME_BACK_REWARD]: handleWelcomeBackReward,
};

const POST_OFFLINE_HANDLERS: Record<string, () => void> = {
	[DeepLinkType.WELCOME_BACK_OFFER]: handleWelcomeBackOffer,
	[DeepLinkType.EVENT]: handleEvent,
	[DeepLinkType.REFERRAL]: handleReferral,
	[DeepLinkType.REFERRAL_DATA]: handleReferralData,
	[DeepLinkType.STATS]: handleStats,
	[DeepLinkType.UPGRADES]: handleUpgrades,
	[DeepLinkType.MARKET]: handleMarket,
	[DeepLinkType.SETTINGS]: handleSettings,
};

/**
 * Checks whether a deep link type should be processed before offline income.
 */
export const isPreOfflineLink = (type: string): boolean => {
	return Object.prototype.hasOwnProperty.call(PRE_OFFLINE_HANDLERS, type);
};

/**
 * Checks whether a deep link type should be processed after offline income.
 */
export const isPostOfflineLink = (type: string): boolean => {
	return Object.prototype.hasOwnProperty.call(POST_OFFLINE_HANDLERS, type);
};

export const processDeepLink = (deepLinkData: string): void => {
	if (!VALID_DEEP_LINK_VALUES.has(deepLinkData as DeepLinkType)) {
		throw new GameError(
			GameErrorType.DATA_ERROR,
			`Unknown deep link type: ${deepLinkData}`,
		);
	}

	// Check if this is a pre-offline deep link
	if (isPreOfflineLink(deepLinkData)) {
		// Fire the handler immediately (before offline income popup)
		PRE_OFFLINE_HANDLERS[deepLinkData]();
		return;
	}

	const postHandlerCallback = (): void => {
		const handler = POST_OFFLINE_HANDLERS[deepLinkData];
		if (handler) {
			handler();
		}
	};

	setPendingPostOfflineCallback(postHandlerCallback);
};
